import type { AlertEvent, AlertRule, Application } from '@owl/shared';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { api } from '../../../lib/api';
import { formatDateTime } from '../../../lib/time';
import { Icon } from '../../icons';
import { ChannelBadge, SeverityChip } from '../../shared';
import { BottomSheet } from '../BottomSheet';
import type { MobileShellCtx } from '../MobileShellCtx';

const LABEL: CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--fg-3)',
  fontWeight: 500,
  marginBottom: 8,
};

function ruleCondition(rule: AlertRule): ReactNode {
  const c = rule.condition;
  switch (c.type) {
    case 'any_match':
      return <>Cualquier evento dentro del scope.</>;
    case 'threshold':
      return (
        <>
          Más de <span className="accent">{c.count}</span> en{' '}
          <span className="accent">{c.windowMinutes}m</span>.
        </>
      );
    case 'content_match':
      return (
        <>
          Match{' '}
          <span className="accent">
            {c.isRegex ? '/' : '"'}
            {c.pattern}
            {c.isRegex ? '/' : '"'}
          </span>{' '}
          en el mensaje.
        </>
      );
    case 'metadata_match':
      return (
        <>
          metadata.<span className="accent">{c.key}</span> {c.operator}{' '}
          <span className="accent">{c.value}</span>
        </>
      );
  }
}

export function AlertEventSheet({ ctx, eventId }: { ctx: MobileShellCtx; eventId: string }) {
  const [event, setEvent] = useState<AlertEvent | null>(null);
  const [rule, setRule] = useState<AlertRule | null>(null);
  const [app, setApp] = useState<Application | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const events = await api.listAlertEvents({ limit: 500 });
        if (cancelled) return;
        const ev = events.items.find((e) => e.id === eventId) ?? null;
        setEvent(ev);
        if (ev) {
          const [r, a] = await Promise.all([
            api.getAlertRule(ev.ruleId).catch(() => null),
            api.getApplication(ev.applicationId).catch(() => null),
          ]);
          if (cancelled) return;
          if (r) setRule(r.rule);
          if (a) setApp(a.application);
        }
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  return (
    <BottomSheet title="Alerta" onClose={ctx.closeSheet}>
      {err && <div className="m-error-banner">{err}</div>}
      {!event && !err && <div className="m-loading">Cargando…</div>}
      {event && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            {rule && <SeverityChip severity={rule.severity} />}
            <span
              className="mono"
              style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--fg-3)' }}
            >
              {formatDateTime(event.triggeredAt)}
            </span>
          </div>

          {rule && (
            <>
              <h3
                style={{
                  margin: '0 0 6px',
                  fontSize: 18,
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                }}
              >
                {rule.name}
              </h3>
              {rule.description && (
                <div
                  style={{
                    color: 'var(--fg-3)',
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    marginBottom: 14,
                  }}
                >
                  {rule.description}
                </div>
              )}

              <div style={LABEL}>Condición</div>
              <div className="rule-cond" style={{ marginBottom: 14 }}>
                {ruleCondition(rule)}
              </div>
            </>
          )}

          {app && (
            <>
              <div style={LABEL}>App afectada</div>
              <button
                type="button"
                className="app-row"
                style={{
                  background: 'var(--bg-2)',
                  border: '1px solid var(--border-1)',
                  borderRadius: 10,
                  marginBottom: 14,
                }}
                onClick={() => ctx.navigate({ name: 'appdash', applicationId: app.id })}
              >
                <div className="app-avatar">{app.product.slice(0, 2).toUpperCase()}</div>
                <div className="info">
                  <div className="name">{app.name}</div>
                  <div className="meta">
                    <span>{app.company}</span>
                    {app.instance && (
                      <>
                        <span className="dot-sep">·</span>
                        <span>{app.instance}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="right">
                  <Icon.ChevronRight size={14} />
                </div>
              </button>
            </>
          )}

          <div style={LABEL}>Notificación</div>
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              marginBottom: event.triggeringLogId ? 14 : 0,
            }}
          >
            {event.channelsResult.length === 0 && (
              <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>—</span>
            )}
            {event.channelsResult.map((c) => (
              <ChannelBadge key={c.channel} channel={c.channel} status={c.status} />
            ))}
          </div>

          {event.triggeringLogId && (
            <button
              type="button"
              onClick={() => {
                if (event.triggeringLogId) {
                  ctx.openSheet({ kind: 'log', logId: event.triggeringLogId });
                }
              }}
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 10,
                background: 'var(--bg-2)',
                border: '1px solid var(--border-2)',
                color: 'var(--fg-1)',
                fontSize: 13,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              Ver log disparador <Icon.ChevronRight size={13} />
            </button>
          )}
        </>
      )}
    </BottomSheet>
  );
}
